// =====================================================================
// Briques d'interface réutilisées par les pages
// =====================================================================

import { useEffect, useState, type ReactNode } from 'react';
import type { Identite, RoleParent, Sexe } from '../../partage/types';
import { lister, surChangement, type EnAttente } from '../file-attente';

// --- Icônes ----------------------------------------------------------
// Inline : une icône qui arrive après le texte fait sauter la mise en page.

type IconeProps = { className?: string };

export const IconeArbre = (p: IconeProps) => (
  <svg {...p} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
       strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="4" r="2.2" />
    <circle cx="5" cy="19" r="2.2" />
    <circle cx="19" cy="19" r="2.2" />
    <path d="M12 6.2v4.3M5 16.8v-2.6h14v2.6M12 10.5v3.7" />
  </svg>
);

export const IconeLiens = (p: IconeProps) => (
  <svg {...p} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
       strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M9.5 14.5 14.5 9.5" />
    <path d="M7 11 5.2 12.8a3.8 3.8 0 0 0 5.4 5.4L12.4 16.4" />
    <path d="M17 13l1.8-1.8a3.8 3.8 0 0 0-5.4-5.4L11.6 7.6" />
  </svg>
);

export const IconeEnveloppe = (p: IconeProps) => (
  <svg {...p} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
       strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="3" y="5" width="18" height="14" rx="2.5" />
    <path d="m3.5 7 8.5 6 8.5-6" />
  </svg>
);

export const IconeMasque = (p: IconeProps) => (
  <svg {...p} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
       strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 5c-4.5 0-8 4.2-9 7 1 2.8 4.5 7 9 7s8-4.2 9-7c-1-2.8-4.5-7-9-7Z" />
    <path d="M4 20 20 4" />
  </svg>
);

export const IconePersonne = (p: IconeProps) => (
  <svg {...p} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
       strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="8" r="3.6" />
    <path d="M4.8 20a7.2 7.2 0 0 1 14.4 0" />
  </svg>
);

export const IconeCadenas = (p: IconeProps) => (
  <svg {...p} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
       strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="4.5" y="10.5" width="15" height="10" rx="2.4" />
    <path d="M8 10.5V7.8a4 4 0 0 1 8 0v2.7" />
  </svg>
);

export const IconeTelephone = (p: IconeProps) => (
  <svg {...p} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
       strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="6.5" y="2.5" width="11" height="19" rx="2.6" />
    <path d="M10.6 18.6h2.8" />
  </svg>
);

export const IconeMicro = (p: IconeProps) => (
  <svg {...p} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
       strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="9" y="2.5" width="6" height="11" rx="3" />
    <path d="M5.5 11a6.5 6.5 0 0 0 13 0M12 17.5V21M9 21h6" />
  </svg>
);

export const IconeQuitter = (p: IconeProps) => (
  <svg {...p} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
       strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
    <path d="M16 17l5-5-5-5M21 12H9" />
  </svg>
);

export const IconeLieu = (p: IconeProps) => (
  <svg {...p} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
       strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 21.5s7-6.2 7-11.2a7 7 0 1 0-14 0c0 5 7 11.2 7 11.2z" />
    <circle cx="12" cy="10" r="2.6" />
  </svg>
);

export const IconeCalendrier = (p: IconeProps) => (
  <svg {...p} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
       strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="3.5" y="5.5" width="17" height="15" rx="2.4" />
    <path d="M3.5 10h17M8 3.4v4M16 3.4v4" />
  </svg>
);

/**
 * La marque. Une image et non plus un tracé : l'illustration de famille
 * ne se redessine pas en quelques traits.
 *
 * `alt` est vide partout où elle apparaît, parce que le mot « Yambi » est
 * toujours écrit juste à côté — le faire lire deux fois à un lecteur
 * d'écran n'apporte rien.
 */
export const IconeMarque = (p: IconeProps) => (
  <img
    className={p.className}
    src="/icone-192.png"
    alt=""
    width={192}
    height={192}
  />
);

// --- Champ avec pictogramme ------------------------------------------
// L'icône est purement visuelle : elle est masquée aux lecteurs d'écran,
// qui annoncent l'étiquette, et elle ne capte pas le clic, qui doit
// toujours poser le curseur dans le champ.

export function Champ({
  id,
  etiquette,
  icone,
  aide,
  children,
}: {
  id: string;
  etiquette: string;
  icone: ReactNode;
  aide?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="champ">
      <label htmlFor={id}>{etiquette}</label>
      <div className="champ-icone">
        {icone}
        {children}
      </div>
      {aide && <p className="doux champ-aide">{aide}</p>}
    </div>
  );
}

// --- Messages --------------------------------------------------------

export function Alerte({
  type = 'erreur',
  children,
}: {
  type?: 'erreur' | 'succes';
  children: ReactNode;
}) {
  return (
    <div className={`alerte-boite ${type}`} role={type === 'erreur' ? 'alert' : 'status'}>
      {children}
    </div>
  );
}

export function Vide({ icone, children }: { icone?: ReactNode; children: ReactNode }) {
  return (
    <div className="vide">
      {icone}
      <p>{children}</p>
    </div>
  );
}

export function Chargement({ texte = 'Chargement…' }: { texte?: string }) {
  return (
    <div className="chargement" role="status">
      {texte}
    </div>
  );
}

// --- Initiales -------------------------------------------------------

export function initiales(nom: string | null, prenoms: string | null): string {
  const a = prenoms?.trim()?.[0] ?? '';
  const b = nom?.trim()?.[0] ?? '';
  return (a + b).toUpperCase() || '?';
}

export function nomComplet(nom: string | null, prenoms: string | null): string {
  const complet = [prenoms, nom].filter(Boolean).join(' ').trim();
  return complet || 'Sans nom';
}

// =====================================================================
// FormulaireIdentite — la saisie d'une personne
// =====================================================================
// Choix délibérés :
//   - seul le nom OU le prénom est exigé ; beaucoup de gens ne
//     connaissent qu'un des deux pour un aïeul ;
//   - la date de naissance est optionnelle et accompagnée de sa
//     précision, parce que la plupart des anciens n'en ont pas ;
//   - le téléphone est présenté pour ce qu'il fait réellement : prévenir
//     la personne, pas l'inscrire à sa place.

export function FormulaireIdentite({
  valeur,
  onChange,
  avecContact = false,
  avecDeces = false,
}: {
  valeur: Identite;
  onChange: (i: Identite) => void;
  /** Propose e-mail et téléphone, pour prévenir la personne ajoutée. */
  avecContact?: boolean;
  avecDeces?: boolean;
}) {
  const maj = (champ: keyof Identite, v: unknown) =>
    onChange({ ...valeur, [champ]: v === '' ? undefined : v });

  return (
    <>
      <div className="duo">
        <Champ id="i-prenoms" etiquette="Prénom(s)" icone={<IconePersonne />}>
          <input
            id="i-prenoms"
            value={valeur.prenoms ?? ''}
            onChange={(e) => maj('prenoms', e.target.value)}
            autoComplete="off"
          />
        </Champ>
        <Champ id="i-nom" etiquette="Nom" icone={<IconePersonne />}>
          <input
            id="i-nom"
            value={valeur.nom ?? ''}
            onChange={(e) => maj('nom', e.target.value)}
            autoComplete="off"
          />
        </Champ>
      </div>

      <fieldset>
        <legend>Sexe</legend>
        <div className="radios">
          {(['M', 'F'] as Sexe[]).map((s) => (
            <label key={s}>
              <input
                type="radio"
                name="i-sexe"
                checked={valeur.sexe === s}
                onChange={() => maj('sexe', s)}
              />
              {s === 'M' ? 'Homme' : 'Femme'}
            </label>
          ))}
        </div>
      </fieldset>

      <Champ
        id="i-village"
        etiquette="Village d'origine"
        icone={<IconeLieu />}
        aide="Le village d'origine, pas le lieu de résidence. C'est l'information qui permet de retrouver votre famille."
      >
        <input
          id="i-village"
          value={valeur.village_origine ?? ''}
          onChange={(e) => maj('village_origine', e.target.value)}
          placeholder="Kananga, Mweka, Lubumbashi…"
          autoComplete="off"
        />
      </Champ>

      <div className="duo">
        <Champ id="i-date" etiquette="Date de naissance" icone={<IconeCalendrier />}>
          <input
            id="i-date"
            type="date"
            value={valeur.date_naissance ?? ''}
            onChange={(e) => maj('date_naissance', e.target.value || null)}
          />
        </Champ>
        <div className="champ">
          <label htmlFor="i-precision">Précision</label>
          <select
            id="i-precision"
            value={valeur.precision_naissance ?? 'exacte'}
            onChange={(e) => maj('precision_naissance', e.target.value)}
            disabled={!valeur.date_naissance}
          >
            <option value="exacte">Date exacte</option>
            <option value="mois">Mois connu</option>
            <option value="annee">Année seulement</option>
            <option value="approximative">Approximative</option>
          </select>
        </div>
      </div>
      {!valeur.date_naissance && (
        <p className="doux" style={{ marginTop: -6, marginBottom: 13 }}>
          Laissez vide si vous ne la connaissez pas. C'est très fréquent et ce
          n'est pas un problème.
        </p>
      )}

      {avecDeces && (
        <div className="champ">
          <div className="radios">
            <label>
              <input
                type="checkbox"
                checked={Boolean((valeur as { decede?: boolean }).decede)}
                onChange={(e) => maj('decede' as keyof Identite, e.target.checked)}
              />
              Cette personne est décédée
            </label>
          </div>
        </div>
      )}

      {avecContact && (
        <fieldset>
          <legend>Pour la prévenir (facultatif)</legend>
          <p className="doux" style={{ marginTop: 0, marginBottom: 11 }}>
            Si cette personne a déjà un compte Yambi, elle recevra « vous avez
            été ajouté comme proche » et devra confirmer. Rien n'est visible
            avant son accord. L'un des deux suffit.
          </p>
          <Champ id="i-email" etiquette="Son adresse e-mail" icone={<IconeEnveloppe />}>
            <input
              id="i-email"
              type="email"
              inputMode="email"
              value={valeur.email ?? ''}
              onChange={(e) => maj('email', e.target.value)}
              placeholder="elle@exemple.com"
              autoComplete="off"
              autoCapitalize="none"
              spellCheck={false}
            />
          </Champ>
          <Champ id="i-tel" etiquette="Son numéro de téléphone" icone={<IconeTelephone />}>
            <input
              id="i-tel"
              type="tel"
              inputMode="tel"
              value={valeur.telephone ?? ''}
              onChange={(e) => maj('telephone', e.target.value)}
              placeholder="+243…, +32…, +27…"
              autoComplete="off"
            />
          </Champ>
        </fieldset>
      )}
    </>
  );
}

/** Vrai si la saisie est suffisante pour être envoyée. */
export function identiteValide(i: Identite): boolean {
  return Boolean(i.nom?.trim() || i.prenoms?.trim());
}

// --- Sélecteur père / mère -------------------------------------------

export function ChoixRole({
  valeur,
  onChange,
  legende,
  libelles = { pere: 'Père', mere: 'Mère' },
  nom = 'role',
}: {
  valeur: RoleParent | null;
  onChange: (r: RoleParent) => void;
  legende: string;
  libelles?: Record<RoleParent, string>;
  nom?: string;
}) {
  return (
    <fieldset>
      <legend>{legende}</legend>
      <div className="radios">
        {(['pere', 'mere'] as RoleParent[]).map((r) => (
          <label key={r}>
            <input
              type="radio"
              name={nom}
              checked={valeur === r}
              onChange={() => onChange(r)}
            />
            {libelles[r]}
          </label>
        ))}
      </div>
    </fieldset>
  );
}

// --- Indicateur hors ligne -------------------------------------------

export function useEnLigne(): boolean {
  const [enLigne, setEnLigne] = useState(
    typeof navigator === 'undefined' ? true : navigator.onLine,
  );

  useEffect(() => {
    const monter = () => setEnLigne(true);
    const descendre = () => setEnLigne(false);
    window.addEventListener('online', monter);
    window.addEventListener('offline', descendre);
    return () => {
      window.removeEventListener('online', monter);
      window.removeEventListener('offline', descendre);
    };
  }, []);

  return enLigne;
}

/**
 * Ce qui attend d'être envoyé. Se rafraîchit à chaque mouvement de la
 * file : IndexedDB n'émet rien de lui-même, c'est `file-attente` qui
 * signale ses changements.
 */
export function useFileAttente(): EnAttente[] {
  const [file, setFile] = useState<EnAttente[]>([]);

  useEffect(() => {
    const relire = () => {
      void lister()
        .then(setFile)
        .catch(() => setFile([]));
    };
    relire();
    return surChangement(relire);
  }, []);

  return file;
}
